<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { VDialog, VMenu } from "vuetify/components";
import DatePicker from "./DatePicker.vue";
import PrimaryButton from "@/components/shared/buttons/PrimaryButton.vue";
import SecondaryButton from "@/components/shared/buttons/SecondaryButton.vue";
import { ref, watch, computed } from "vue";

const props = defineProps<{
  min?: Date | string;
  max?: Date | string;
  allowedDates?: unknown[] | ((date: unknown) => boolean);
}>();

const selectedDate = defineModel<Date | string | any>("selectedDate");
const localValue = ref(selectedDate.value);
const menu = defineModel<boolean>("menu");
const { t } = useI18n();
const { smAndDown } = useDisplay();

const overlayComponent = computed(() => (smAndDown.value ? VDialog : VMenu));
const overlayProps = computed(() =>
  smAndDown.value
    ? { maxWidth: 400 }
    : {
        closeOnContentClick: false,
        nudgeRight: 40,
        transition: "scale-transition",
        offsetY: true,
        minWidth: "auto",
        scrollStrategy: "block" as const,
      }
);

const save = () => {
  selectedDate.value = localValue.value;
  menu.value = false;
};

const cancel = () => {
  localValue.value = selectedDate.value;
  menu.value = false;
};

watch(
  () => selectedDate.value,
  (val) => {
    if (!menu.value) {
      localValue.value = val;
    }
  }
);
</script>

<template>
  <component
    :is="overlayComponent"
    v-model="menu"
    v-bind="overlayProps"
  >
    <template #activator="{ props: menuProps }">
      <slot
        name="activator"
        v-bind="{ menuProps, selectedDate }"
      />
    </template>
    <date-picker
      v-bind="props"
      v-model:selected-date="localValue"
    >
      <template #actions>
        <secondary-button
          tone="neutral"
          data-testid="cancel-date-button"
          @click="cancel"
        >
          {{ t("generic.action.cancel") }}
        </secondary-button>
        <primary-button
          data-testid="save-date-button"
          @click="save"
        >
          {{ t("generic.action.save") }}
        </primary-button>
      </template>
    </date-picker>
  </component>
</template>
